<template>
  <div class="admin-content-container">
    <div class="space-y-8">
      <div class="flex justify-between items-center">
        <h2 class="font-header text-[3rem] font-bold text-primary">Promotional Banners</h2>
        <button v-if="!showBannerTypeSelection && !showForm" @click="showBannerTypeSelection = true; bannerType = null"
          class="px-6 py-3 bg-accent text-white rounded-md hover:bg-green-700 transition-colors font-body text-[1.125rem] leading-[1.75]">
          Add New Banner
        </button>
      </div>

      <!-- Banner Type Selection Screen -->
      <div v-if="showBannerTypeSelection && !bannerType" class="bg-white rounded-lg shadow-sm p-8 md:p-12">
        <div class="flex justify-between items-center mb-8">
          <h3 class="font-header text-[1.5rem] font-bold text-primary">Select Banner Type</h3>
          <button @click="showBannerTypeSelection = false"
            class="text-gray-600 hover:text-gray-800 font-body text-[1.125rem] leading-[1.75]">
            Cancel
          </button>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
          <button
            @click="bannerType = 'text'; showBannerTypeSelection = false; showForm = true; editingBanner = null; editingTextBanner = null"
            class="p-8 border-2 border-accent rounded-lg hover:bg-creme-50 transition-all hover:shadow-md">
            <div class="text-center">
              <div class="text-5xl mb-4">📝</div>
              <h4 class="font-header text-[1.5rem] font-bold text-primary mb-3">Text Banner</h4>
              <p class="font-body text-[1.125rem] leading-[1.75] text-gray-600">Create a custom text banner with
                customizable colors</p>
            </div>
          </button>
          <button
            @click="bannerType = 'image'; showBannerTypeSelection = false; showForm = true; editingBanner = null; editingTextBanner = null"
            class="p-8 border-2 border-accent rounded-lg hover:bg-creme-50 transition-all hover:shadow-md">
            <div class="text-center">
              <div class="text-5xl mb-4">🖼️</div>
              <h4 class="font-header text-[1.5rem] font-bold text-primary mb-3">Image Banner</h4>
              <p class="font-body text-[1.125rem] leading-[1.75] text-gray-600">Upload an image with cropping controls,
                side-by-side content layout, and height controls</p>
            </div>
          </button>
        </div>
      </div>

      <!-- Text Banner Form -->
      <div v-if="showForm && bannerType === 'text'" class="bg-white rounded-lg shadow-sm p-8 md:p-12">
        <div class="flex justify-between items-center mb-8">
          <h3 class="font-header text-[1.5rem] font-bold text-primary">Promotional Text Banner</h3>
          <button @click="cancelForm()"
            class="font-body text-[1.125rem] leading-[1.75] text-gray-600 hover:text-gray-800">
            Cancel
          </button>
        </div>

        <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div>
            <form @submit.prevent="savePromotionalBanner" class="space-y-6">
              <div>
                <label class="block font-body text-[1.125rem] leading-[1.75] text-primary mb-2">Banner Text *</label>
                <input v-model="promotionalBanner.text" type="text" required
                  class="w-full px-4 py-3 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-body text-[1.125rem] leading-[1.75]"
                  placeholder="Special Offer: 20% off all books this month!" />
                <BannerLinkEditor v-if="promotionalBanner.text" :banner-text="promotionalBanner.text"
                  :links="promotionalBanner.links || []" @update:links="promotionalBanner.links = $event"
                  class="mt-4" />
              </div>
              <div>
                <label class="block font-body text-[1.125rem] leading-[1.75] text-primary mb-2">Banner Subtext</label>
                <input v-model="promotionalBanner.subtext" type="text"
                  class="w-full px-4 py-3 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-body text-[1.125rem] leading-[1.75]"
                  placeholder="Use code READ20 at checkout" />
              </div>
              <div>
                <label class="block font-body text-[1.125rem] leading-[1.75] text-primary mb-3">Background Color</label>
                <ColorPicker v-model="promotionalBanner.backgroundColor" />
                <p class="font-body text-[1.125rem] leading-[1.75] text-gray-600 mt-2">Selected color: {{
                  promotionalBanner.backgroundColor }}</p>
              </div>
              <div>
                <label class="block font-body text-[1.125rem] leading-[1.75] text-primary mb-3">Text Color</label>
                <ColorPicker v-model="promotionalBanner.textColor" />
                <p class="font-body text-[1.125rem] leading-[1.75] text-gray-600 mt-2">Selected color: {{
                  promotionalBanner.textColor }}</p>
              </div>
              <div>
                <label class="block font-body text-[1.125rem] leading-[1.75] text-primary mb-2">Position *</label>
                <select v-model="promotionalBanner.position" required
                  class="w-full px-4 py-3 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-body text-[1.125rem] leading-[1.75]">
                  <option value="top">Top (Before Navbar)</option>
                  <option value="bottom">Bottom (Before Footer)</option>
                </select>
              </div>
              <div>
                <label class="block text-sm font-marketing font-semibold text-primary mb-3">Pages</label>
                <div class="max-h-48 overflow-y-auto border border-gray-300 rounded-md p-4 bg-white">
                  <label
                    class="flex items-center gap-3 mb-3 p-2 hover:bg-creme-50 rounded cursor-pointer transition-colors">
                    <input type="checkbox" :checked="!promotionalBanner.pages || promotionalBanner.pages.length === 0"
                      @change="promotionalBanner.pages = []"
                      class="w-4 h-4 bg-white border-gray-300 text-gray-900 focus:ring-gray-400 focus:ring-2 rounded" />
                    <span class="text-sm font-sans">All Pages</span>
                  </label>
                  <div class="border-t border-gray-200 my-3"></div>
                  <label v-for="page in availablePages" :key="page.value"
                    class="flex items-center gap-3 mb-2 p-2 hover:bg-creme-50 rounded cursor-pointer transition-colors">
                    <input type="checkbox" :value="page.value" v-model="promotionalBanner.pages"
                      class="w-4 h-4 bg-white border-gray-300 text-gray-900 focus:ring-gray-400 focus:ring-2 rounded" />
                    <span class="text-sm font-sans">{{ page.label }}</span>
                  </label>
                </div>
                <p class="text-xs text-gray-600 mt-2 font-sans">
                  Selected: {{ (!promotionalBanner.pages || promotionalBanner.pages.length === 0) ? 'All Pages' :
                    promotionalBanner.pages.join(', ') }}
                </p>
              </div>
              <div>
                <label class="flex items-center gap-3">
                  <input v-model="promotionalBanner.isActive" type="checkbox"
                    class="w-4 h-4 bg-white border-gray-300 text-gray-900 focus:ring-gray-400 focus:ring-2 rounded" />
                  <span class="text-sm font-marketing font-semibold text-primary">Active</span>
                </label>
              </div>
              <div class="flex gap-3 pt-4 border-t border-gray-200">
                <button type="submit"
                  class="px-8 py-3 bg-accent text-white rounded-md hover:bg-green-700 transition-colors font-marketing font-semibold">
                  Save Promotional Banner
                </button>
              </div>
            </form>

            <div class="mt-8 p-6 bg-creme-50 rounded-md">
              <h4 class="text-sm font-marketing font-bold text-primary mb-3">Banner Dimensions</h4>
              <div class="text-sm font-sans space-y-2 text-gray-700">
                <p><strong>Width:</strong> 100% (Full width)</p>
                <p><strong>Height:</strong> Auto (based on content)</p>
                <p><strong>Padding:</strong> 16px (1rem) vertical, 16px (1rem) horizontal</p>
                <p><strong>Background:</strong> <span :style="{ color: promotionalBanner.backgroundColor }">{{
                  promotionalBanner.backgroundColor }}</span></p>
                <p><strong>Max Width:</strong> 1280px (7xl container)</p>
              </div>
            </div>
          </div>

          <div>
            <h4 class="text-lg font-marketing font-bold text-primary mb-4">Preview</h4>
            <div class="bg-white border border-gray-300 rounded-md overflow-hidden shadow-sm">
              <div class="p-4 bg-creme-50 border-b border-creme-200">
                <p class="text-xs font-sans text-gray-600 font-semibold">Live Preview</p>
              </div>
              <div class="p-6">
                <Banner v-if="promotionalBanner.text" :banner-text="promotionalBanner.text"
                  :banner-subtext="promotionalBanner.subtext" :background-color="promotionalBanner.backgroundColor"
                  :text-color="promotionalBanner.textColor" :links="promotionalBanner.links || []"
                  :dismissible="false" />
                <div v-else class="w-full py-8 px-4 border-2 border-dashed border-gray-300 bg-gray-50 rounded-md">
                  <div class="max-w-7xl mx-auto">
                    <p class="text-center text-gray-400 font-sans text-sm">Enter banner text to see preview</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Image Banner Form -->
      <EnhancedBannerEditor v-if="showForm && bannerType === 'image'" :editing-banner="editingBanner"
        @cancel="cancelForm" @saved="handleBannerSaved" @preview-updated="$emit('preview-updated', $event)" />

      <!-- Old Image Banner Form (keeping for reference but not used) -->
      <div v-if="false && showForm && bannerType === 'image'" class="bg-white rounded-lg shadow-sm p-8 md:p-12">
        <div class="flex justify-between items-center mb-8">
          <h3 class="text-2xl md:text-3xl font-marketing font-bold text-primary">Image Banner</h3>
          <button @click="cancelForm()" class="text-gray-600 hover:text-gray-800 font-marketing font-semibold">
            Cancel
          </button>
        </div>

        <form @submit.prevent="saveBanner" class="space-y-6">
          <div>
            <label class="block text-sm font-marketing font-semibold text-primary mb-2">Title *</label>
            <input v-model="formData.title" type="text" required
              class="w-full px-4 py-3 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-sans" />
          </div>
          <div>
            <label class="block text-sm font-marketing font-semibold text-primary mb-2">Description</label>
            <textarea v-model="formData.description" rows="4"
              class="w-full px-4 py-3 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-sans resize-none"></textarea>
          </div>
          <div>
            <label class="block text-sm font-marketing font-semibold text-primary mb-2">Banner Image *</label>
            <div class="space-y-3">
              <div class="flex gap-3">
                <input ref="bannerFileInput" type="file" accept="image/*,.heic,.heif" @change="handleBannerFileSelect"
                  class="hidden" />
                <button type="button" @click="() => bannerFileInput?.click()"
                  class="px-6 py-3 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors font-marketing font-semibold">
                  Upload Image
                </button>
                <input v-model="formData.image_url" type="url" :required="!bannerImagePreview && !formData.image_url"
                  class="flex-1 px-4 py-3 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-sans"
                  placeholder="Or enter image URL" />
              </div>
              <div v-if="bannerImagePreview" class="mt-3">
                <img :src="bannerImagePreview" alt="Banner Preview"
                  class="max-w-md h-48 object-cover border border-creme-300" />
                <button type="button" @click="clearBannerImage"
                  class="mt-2 text-sm text-red-600 hover:text-red-700 font-marketing">
                  Remove Image
                </button>
              </div>
              <div v-else-if="formData.image_url && !uploadingBanner" class="mt-3">
                <img :src="formData.image_url" alt="Banner Preview"
                  class="max-w-md h-48 object-cover border border-creme-300" />
              </div>
              <div v-if="uploadingBanner" class="text-sm text-gray-600 font-sans">
                Uploading...
              </div>
            </div>
          </div>
          <div>
            <label class="block text-sm font-marketing font-semibold text-primary mb-2">Link URL</label>
            <input v-model="formData.link_url" type="url"
              class="w-full px-4 py-3 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-sans"
              placeholder="https://example.com" />
          </div>
          <div>
            <label class="block text-sm font-marketing font-semibold text-primary mb-2">Link Text</label>
            <input v-model="formData.link_text" type="text"
              class="w-full px-4 py-3 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-sans"
              placeholder="Learn More" />
          </div>
          <div>
            <label class="block text-sm font-marketing font-semibold text-primary mb-2">Position *</label>
            <select v-model="formData.position" required
              class="w-full px-4 py-3 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-sans">
              <option value="top">Top</option>
              <option value="bottom">Bottom</option>
            </select>
          </div>
          <div>
            <label class="block text-sm font-marketing font-semibold text-primary mb-3">Pages</label>
            <div class="max-h-48 overflow-y-auto border border-gray-300 rounded-md p-4 bg-white">
              <label
                class="flex items-center gap-3 mb-3 p-2 hover:bg-creme-50 rounded cursor-pointer transition-colors">
                <input type="checkbox" :checked="!formData.pages || formData.pages.length === 0"
                  @change="formData.pages = []"
                  class="w-4 h-4 bg-white border-gray-300 text-gray-900 focus:ring-gray-400 focus:ring-2 rounded" />
                <span class="font-body text-[1.125rem] leading-[1.75]">All Pages</span>
              </label>
              <div class="border-t border-gray-200 my-3"></div>
              <label v-for="page in availablePages" :key="page.value"
                class="flex items-center gap-3 mb-2 p-2 hover:bg-creme-50 rounded cursor-pointer transition-colors">
                <input type="checkbox" :value="page.value" v-model="formData.pages"
                  class="w-4 h-4 bg-white border-gray-300 text-gray-900 focus:ring-gray-400 focus:ring-2 rounded" />
                <span class="text-sm font-sans">{{ page.label }}</span>
              </label>
            </div>
            <p class="text-xs text-gray-600 mt-2 font-sans">
              Selected: {{ (!formData.pages || formData.pages.length === 0) ? 'All Pages' : formData.pages.join(', ') }}
            </p>
          </div>
          <div>
            <label class="flex items-center gap-3">
              <input v-model="formData.is_active" type="checkbox"
                class="w-4 h-4 bg-white border-gray-300 text-gray-900 focus:ring-gray-400 focus:ring-2 rounded" />
              <span class="text-sm font-marketing font-semibold text-primary">Active</span>
            </label>
          </div>
          <div class="flex gap-3 pt-4 border-t border-gray-200">
            <button type="submit"
              class="px-8 py-3 bg-accent text-white rounded-md hover:bg-green-700 transition-colors font-marketing font-semibold">
              {{ editingBanner ? 'Update Banner' : 'Create Banner' }}
            </button>
            <button type="button" @click="cancelForm()"
              class="px-8 py-3 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors font-marketing font-semibold">
              Cancel
            </button>
          </div>
        </form>
      </div>

      <div v-if="!showForm && !showBannerTypeSelection" class="space-y-8">
        <div class="bg-white rounded-lg shadow-sm overflow-hidden">
          <div class="p-6 border-b border-gray-200">
            <h3 class="text-xl font-marketing font-bold text-primary">Image Banners</h3>
          </div>
          <div class="overflow-x-auto">
            <table class="w-full">
              <thead class="bg-accent text-white">
                <tr>
                  <th class="px-6 py-4 text-left font-marketing font-semibold">Title</th>
                  <th class="px-6 py-4 text-left font-marketing font-semibold">Position</th>
                  <th class="px-6 py-4 text-left font-marketing font-semibold">Pages</th>
                  <th class="px-6 py-4 text-left font-marketing font-semibold">Status</th>
                  <th class="px-6 py-4 text-left font-marketing font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="banners.length === 0">
                  <td colspan="5" class="px-6 py-12 text-center text-gray-500 font-sans">
                    No image banners found.
                  </td>
                </tr>
                <tr v-for="banner in banners" :key="banner.id"
                  class="border-b border-gray-200 hover:bg-creme-50 transition-colors">
                  <td class="px-6 py-4 font-marketing text-gray-900 font-medium">{{ banner.title }}</td>
                  <td class="px-6 py-4 font-sans capitalize text-gray-700">{{ banner.position === 'top' ? 'Top' :
                    'Bottom' }}</td>
                  <td class="px-6 py-4 font-sans text-sm text-gray-700">
                    {{ formatPages(banner.pages) }}
                  </td>
                  <td class="px-6 py-4 font-sans">
                    <span :class="banner.is_active ? 'text-accent font-semibold' : 'text-red-600 font-semibold'">
                      {{ banner.is_active ? 'Active' : 'Inactive' }}
                    </span>
                  </td>
                  <td class="px-6 py-4">
                    <div class="flex gap-4">
                      <button @click="editBanner(banner)"
                        class="text-accent hover:text-green-700 font-marketing font-semibold transition-colors">Edit</button>
                      <button @click="toggleStatus(banner)"
                        :class="banner.is_active ? 'text-yellow-600 hover:text-yellow-700' : 'text-accent hover:text-green-700'"
                        class="font-marketing font-semibold transition-colors">
                        {{ banner.is_active ? 'Deactivate' : 'Activate' }}
                      </button>
                      <button @click="deleteBanner(banner.id)"
                        class="text-red-600 hover:text-red-700 font-marketing font-semibold transition-colors">Delete</button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-if="pagination && pagination.total_pages > 1"
            class="p-4 flex justify-between items-center border-t border-creme-200">
            <button @click="loadBanners(currentPage - 1)" :disabled="!pagination.prev_page"
              :class="['px-4 py-2 rounded-md font-marketing', pagination.prev_page ? 'bg-accent text-white hover:bg-green-700' : 'bg-gray-300 text-gray-500 cursor-not-allowed']">
              Previous
            </button>
            <span class="font-marketing text-gray-700">
              Page {{ pagination.page }} of {{ pagination.total_pages }} ({{ pagination.total }} total)
            </span>
            <button @click="loadBanners(currentPage + 1)" :disabled="!pagination.next_page"
              :class="['px-4 py-2 rounded-md font-marketing', pagination.next_page ? 'bg-accent text-white hover:bg-green-700' : 'bg-gray-300 text-gray-500 cursor-not-allowed']">
              Next
            </button>
          </div>
        </div>

        <div class="bg-white rounded-lg shadow-sm overflow-hidden">
          <div class="p-6 border-b border-gray-200">
            <h3 class="text-xl font-marketing font-bold text-primary">Text Banners</h3>
          </div>
          <div class="overflow-x-auto">
            <table class="w-full">
              <thead class="bg-accent text-white">
                <tr>
                  <th class="px-6 py-4 text-left font-marketing font-semibold">Text</th>
                  <th class="px-6 py-4 text-left font-marketing font-semibold">Subtext</th>
                  <th class="px-6 py-4 text-left font-marketing font-semibold">Position</th>
                  <th class="px-6 py-4 text-left font-marketing font-semibold">Pages</th>
                  <th class="px-6 py-4 text-left font-marketing font-semibold">Status</th>
                  <th class="px-6 py-4 text-left font-marketing font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="textBanners.length === 0">
                  <td colspan="6" class="px-6 py-12 text-center text-gray-500 font-sans">
                    No text banners found.
                  </td>
                </tr>
                <tr v-for="banner in textBanners" :key="banner.id"
                  class="border-b border-gray-200 hover:bg-creme-50 transition-colors">
                  <td class="px-6 py-4 font-marketing text-gray-900 font-medium">{{ banner.text }}</td>
                  <td class="px-6 py-4 font-sans text-gray-700">{{ banner.subtext || '-' }}</td>
                  <td class="px-6 py-4 font-sans capitalize text-gray-700">
                    {{ banner.position === 'top' ? 'Top' : 'Bottom' }}
                  </td>
                  <td class="px-6 py-4 font-sans text-sm text-gray-700">
                    {{ formatPages(banner.pages) }}
                  </td>
                  <td class="px-6 py-4 font-sans">
                    <span :class="banner.is_active ? 'text-accent font-semibold' : 'text-red-600 font-semibold'">
                      {{ banner.is_active ? 'Active' : 'Inactive' }}
                    </span>
                  </td>
                  <td class="px-6 py-4">
                    <div class="flex gap-4">
                      <button @click="editTextBanner(banner)"
                        class="text-accent hover:text-green-700 font-marketing font-semibold transition-colors">Edit</button>
                      <button @click="toggleTextBannerStatus(banner)"
                        :class="banner.is_active ? 'text-yellow-600 hover:text-yellow-700' : 'text-accent hover:text-green-700'"
                        class="font-marketing font-semibold transition-colors">
                        {{ banner.is_active ? 'Deactivate' : 'Activate' }}
                      </button>
                      <button @click="deleteTextBanner(banner.id)"
                        class="text-red-600 hover:text-red-700 font-marketing font-semibold transition-colors">Delete</button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { adminService } from '../../services/admin'
import { useScrollToTop } from '../../composables/useScrollToTop'

import Banner from '../Banner.vue'
import ColorPicker from './ColorPicker.vue'
import BannerLinkEditor from './BannerLinkEditor.vue'
import EnhancedBannerEditor from './EnhancedBannerEditor.vue'

const { scrollToTop, watchAndScroll } = useScrollToTop()

const emit = defineEmits(['preview-updated'])

const banners = ref([])
const textBanners = ref([])
const currentPage = ref(1)
const limit = ref(10)
const pagination = ref(null)
const showForm = ref(false)

// Watch showForm and scroll to top when it becomes true
watchAndScroll(showForm)

const showBannerTypeSelection = ref(false)
const bannerType = ref(null) // 'text' or 'image'
const editingBanner = ref(null)
const editingTextBanner = ref(null)
const textBannerId = ref(null)
const bannerFileInput = ref(null)
const bannerImagePreview = ref('')
const uploadingBanner = ref(false)
const promotionalBanner = ref({
  text: '',
  subtext: '',
  backgroundColor: '#FEF3C7',
  textColor: '#111111',
  links: [],
  position: 'top',
  pages: [],
  isActive: true
})

const availablePages = [
  { value: 'home', label: 'Home' },
  { value: 'books', label: 'Books' },
  { value: 'book', label: 'Book Detail' },
  { value: 'authors', label: 'Authors' },
  { value: 'author', label: 'Author Detail' },
  { value: 'about', label: 'About' },
  { value: 'partners', label: 'Partners' },
  { value: 'contact', label: 'Contact' },
  { value: 'donate', label: 'Donate' },
  { value: 'events', label: 'Events' },
  { value: 'event-detail', label: 'Event Detail' },
  { value: 'careers', label: 'Careers' },
  { value: 'press', label: 'Press' },
  { value: 'submissions', label: 'Submissions' }
]


const formData = ref({
  title: '',
  image_url: '',
  link_url: '',
  link_text: '',
  description: '',
  position: 'top',
  pages: [],
  is_active: true
})

const formatPages = (pages) => {
  if (!pages || !Array.isArray(pages) || pages.length === 0) {
    return 'All Pages'
  }
  return pages.join(', ')
}

const loadBanners = async (page = 1) => {
  try {
    const response = await adminService.getBanners(page, limit.value)
    if (response.data && response.pagination) {
      banners.value = (response.data || []).map(banner => ({
        ...banner,
        pages: Array.isArray(banner.pages) ? banner.pages : []
      }))
      pagination.value = response.pagination
      currentPage.value = page
    } else {
      banners.value = []
      pagination.value = null
    }
  } catch (error) {
    console.error('Failed to load banners:', error)
    banners.value = []
    pagination.value = null
  }
}

const loadTextBanners = async () => {
  try {
    const data = await adminService.getPromotionalTextBanners()
    textBanners.value = (Array.isArray(data) ? data : []).map(banner => ({
      ...banner,
      pages: Array.isArray(banner.pages) ? banner.pages : []
    }))
  } catch (error) {
    console.error('Failed to load text banners:', error)
    textBanners.value = []
  }
}

const loadPromotionalBanner = async () => {
  try {
    const data = await adminService.getPromotionalTextBanner()
    if (data) {
      textBannerId.value = data.id
      promotionalBanner.value = {
        text: data.text || '',
        subtext: data.subtext || '',
        backgroundColor: data.background_color || '#FEF3C7',
        textColor: data.text_color || '#111111',
        links: Array.isArray(data.links) ? data.links : [],
        position: data.position || 'top',
        pages: Array.isArray(data.pages) ? data.pages : [],
        isActive: data.is_active !== false
      }
    } else {
      textBannerId.value = null
      promotionalBanner.value = {
        text: '',
        subtext: '',
        backgroundColor: '#FEF3C7',
        textColor: '#111111',
        linkUrl: '',
        position: 'top',
        pages: [],
        isActive: true
      }
    }
  } catch (error) {
    console.error('Failed to load promotional text banner:', error)
    textBannerId.value = null
    promotionalBanner.value = {
      text: '',
      subtext: '',
      backgroundColor: '#FEF3C7',
      textColor: '#111111',
      links: [],
      position: 'top',
      isActive: true
    }
  }
}

const savePromotionalBanner = async () => {
  try {
    if (!promotionalBanner.value.text || promotionalBanner.value.text.trim() === '') {
      alert('Please enter banner text')
      return
    }

    const bannerData = {
      text: promotionalBanner.value.text.trim(),
      subtext: promotionalBanner.value.subtext?.trim() || null,
      background_color: promotionalBanner.value.backgroundColor,
      text_color: promotionalBanner.value.textColor || '#111111',
      links: promotionalBanner.value.links || [],
      position: promotionalBanner.value.position,
      pages: promotionalBanner.value.pages && promotionalBanner.value.pages.length > 0 ? promotionalBanner.value.pages : [],
      is_active: promotionalBanner.value.isActive
    }

    if (textBannerId.value) {
      await adminService.updatePromotionalTextBanner(textBannerId.value, bannerData)
    } else {
      const result = await adminService.createPromotionalTextBanner(bannerData)
      textBannerId.value = result.id
    }
    alert('Promotional text banner saved successfully!')
    await loadTextBanners()
    cancelForm()
  } catch (error) {
    console.error('Failed to save promotional text banner:', error)
    const errorMessage = error.response?.data?.error || error.message || 'Unknown error'
    alert(`Failed to save promotional text banner: ${errorMessage}`)
  }
}

const editBanner = (banner) => {
  bannerType.value = 'image'
  showBannerTypeSelection.value = false
  showForm.value = true
  editingBanner.value = banner

  formData.value = {
    title: banner.title,
    image_url: banner.image_url || '',
    link_url: banner.link_url || '',
    link_text: banner.link_text || '',
    description: banner.description || '',
    position: banner.position,
    pages: Array.isArray(banner.pages) ? banner.pages : [],
    is_active: banner.is_active !== false
  }
}

const handleBannerSaved = async () => {
  await loadBanners(currentPage.value)
  cancelForm()
}

const cancelForm = () => {
  showForm.value = false
  showBannerTypeSelection.value = false
  bannerType.value = null
  editingBanner.value = null
  editingTextBanner.value = null
  textBannerId.value = null
  bannerImagePreview.value = ''
  uploadingBanner.value = false
  emit('preview-updated', null)
  formData.value = {
    title: '',
    image_url: '',
    link_url: '',
    link_text: '',
    description: '',
    position: 'top',
    pages: [],
    is_active: true
  }
  promotionalBanner.value = {
    text: '',
    subtext: '',
    backgroundColor: '#FEF3C7',
    position: 'top',
    pages: [],
    isActive: true
  }
  if (bannerFileInput.value) {
    bannerFileInput.value.value = ''
  }
  scrollToTop()
}

const saveBanner = async () => {
  try {
    if (!formData.value.image_url || formData.value.image_url.trim() === '') {
      alert('Please upload an image or provide an image URL')
      return
    }

    const bannerData = {
      title: formData.value.title,
      image_url: formData.value.image_url || null,
      link_url: formData.value.link_url || null,
      link_text: formData.value.link_text || null,
      description: formData.value.description || null,
      position: formData.value.position,
      pages: formData.value.pages && formData.value.pages.length > 0 ? formData.value.pages : [],
      is_active: formData.value.is_active !== false
    }

    if (editingBanner.value) {
      await adminService.updateBanner(editingBanner.value.id, bannerData)
      alert('Banner updated successfully!')
    } else {
      await adminService.createBanner(bannerData)
      alert('Banner created successfully!')
    }
    await loadBanners(currentPage.value)
    cancelForm()
    scrollToTop()
  } catch (error) {
    console.error('Failed to save banner:', error)
    const errorMessage = error.response?.data?.error || error.message || 'Unknown error'
    alert(`Failed to save banner: ${errorMessage}`)
  }
}

const toggleStatus = async (banner) => {
  try {
    await adminService.toggleBannerStatus(banner.id)
    await loadBanners(currentPage.value)
  } catch (error) {
    console.error('Failed to toggle banner status:', error)
    alert('Failed to toggle banner status. Please try again.')
  }
}

const deleteBanner = async (id) => {
  if (!confirm('Are you sure you want to delete this banner?')) return

  try {
    await adminService.deleteBanner(id)
    await loadBanners(currentPage.value)
  } catch (error) {
    console.error('Failed to delete banner:', error)
    alert('Failed to delete banner. Please try again.')
  }
}

const editTextBanner = (banner) => {
  bannerType.value = 'text'
  showBannerTypeSelection.value = false
  showForm.value = true
  editingTextBanner.value = banner
  textBannerId.value = banner.id
  promotionalBanner.value = {
    text: banner.text || '',
    subtext: banner.subtext || '',
    backgroundColor: banner.background_color || '#FEF3C7',
    textColor: banner.text_color || '#111111',
    links: Array.isArray(banner.links) ? banner.links : [],
    position: banner.position || 'top',
    pages: Array.isArray(banner.pages) ? banner.pages : [],
    isActive: banner.is_active !== false
  }
}

const toggleTextBannerStatus = async (banner) => {
  try {
    await adminService.togglePromotionalTextBannerStatus(banner.id)
    await loadTextBanners()
  } catch (error) {
    console.error('Failed to toggle text banner status:', error)
    alert('Failed to toggle text banner status. Please try again.')
  }
}

const deleteTextBanner = async (id) => {
  if (!confirm('Are you sure you want to delete this text banner?')) return

  try {
    await adminService.deletePromotionalTextBanner(id)
    await loadTextBanners()
    if (textBannerId.value === id) {
      textBannerId.value = null
      promotionalBanner.value = {
        text: '',
        subtext: '',
        backgroundColor: '#FEF3C7',
        textColor: '#111111',
        links: [],
        isActive: true
      }
    }
  } catch (error) {
    console.error('Failed to delete text banner:', error)
    alert('Failed to delete text banner. Please try again.')
  }
}

const handleBannerFileSelect = async (event) => {
  const file = event.target.files[0]
  if (!file) return

  const isImage = file.type.startsWith('image/') ||
    file.name.toLowerCase().endsWith('.heic') ||
    file.name.toLowerCase().endsWith('.heif')
  if (!isImage) {
    alert('Please select an image file')
    return
  }

  if (file.size > 10 * 1024 * 1024) {
    alert('Image size must be less than 10MB')
    return
  }

  uploadingBanner.value = true

  try {
    const { convertHeicForUpload, convertHeicForPreview } = await import('../../utils/heicConverter')
    const fileToUpload = await convertHeicForUpload(file)
    bannerImagePreview.value = await convertHeicForPreview(file)

    const formDataUpload = new FormData()
    formDataUpload.append('file', fileToUpload)

    const uploadedUrl = await adminService.uploadBannerImage(formDataUpload)
    if (uploadedUrl) {
      formData.value.image_url = uploadedUrl
      uploadingBanner.value = false
    } else {
      throw new Error('No URL returned from server')
    }
  } catch (error) {
    console.error('Failed to upload banner image:', error)
    const errorMessage = error.response?.data?.error || error.message || 'Failed to upload image. Please try again.'
    alert(errorMessage)
    uploadingBanner.value = false
    bannerImagePreview.value = ''
    if (bannerFileInput.value) {
      bannerFileInput.value.value = ''
    }
  }
}

const clearBannerImage = () => {
  formData.value.image_url = ''
  bannerImagePreview.value = ''
  if (bannerFileInput.value) {
    bannerFileInput.value.value = ''
  }
}

onMounted(() => {
  loadBanners(1)
  loadPromotionalBanner()
  loadTextBanners()
})
</script>

<style scoped>
.admin-content-container {
  width: 100%;
  min-height: 100vh;
  background-color: #FFFFFF;
  padding: 2rem;
  box-sizing: border-box;
}
</style>
